import type { PluginsInjections } from 'nuxt-i18n-micro'

import {
  ErrorLocaleKey,
  ErrorTag,
  ErrorTypes,
} from '@layers/web/constants/error.ts'
import type { BookErrorScreen } from '@layers/web/stores/types/book.ts'
import type { ConfirmationFetchError, ErrorMessages } from '@layers/web/services/types/error.ts'
import type { ErrorReportBody } from '@layers/web/services/types/booking-api.ts'
import {
  CalendarTimeoutError,
  WaitinglistError,
  ServiceDownError,
} from '@layers/web/services/error.ts'

/**
 * Checks if a request was aborted, for any reason — a caller cancelling it or its own timeout
 * running out. Both surface as an abort, so this is the right guard for "don't retry", but NOT
 * for "don't report": use isManualAbortError for that, since a timeout IS actionable.
 */
const isAbortError = (error: any): boolean => {
  if (error?.name === ErrorTypes.PACKAGE_SEARCH_ABORTED) {
    return true
  }

  const { errorMessage } = getErrorMessage(error)
  return errorMessage.toLowerCase().includes('abort')
}

/**
 * Checks if an abort came from the request's own timeout rather than from a caller cancelling it.
 * Timeout aborts carry the name 'TimeoutError' — both ofetch's built-in timeout and our
 * createTimeoutSignal use it — and ofetch re-wraps the underlying reason as `cause`, so look at
 * both. The message check is the fallback for anything that only survives as text.
 */
const isTimeoutAbortError = (error: any): boolean => {
  if ([error?.name, error?.cause?.name].includes(ErrorTypes.TIMEOUT_ABORT)) {
    return true
  }

  const { errorMessage } = getErrorMessage(error)
  return errorMessage.includes(ErrorTypes.TIMEOUT_ABORT_MESSAGE)
}

/**
 * Checks if a request was cancelled by a caller — a superseded AbortController, a component
 * tearing down, a new search replacing the previous one. Nothing went wrong, so these must NOT
 * be reported to Sentry or the error-report endpoint. A timeout on the same controller
 * deliberately does not match: that is a real failure and stays reportable.
 */
const isManualAbortError = (error: any): boolean =>
  isAbortError(error) && !isTimeoutAbortError(error)

/**
 * Checks if error is a calendar-specific timeout error
 * Status is 400 but localeKey indicates it's actually a timeout
 */
const isCalendarTimeoutError = (error: any): boolean => {
  const localeKey = error?.data?.localeKey || error?.localeKey
  return localeKey === ErrorLocaleKey.CALENDAR_TIMEOUT
}

/**
 * Checks if error is a genuine 5xx server error (the backend actually responded with,
 * or reported, a server error).
 *
 * A no-response error (ofetch's "<no response>") is deliberately NOT treated as service-down:
 * the request never reached the server, so it says nothing about backend health. Those are
 * client-side transport failures — see isTransientNetworkError (retried, then suppressed). (IT-2113)
 */
const isServiceDownError = (error: any): boolean => {
  const status = error?.status || error?.statusCode || error?.response?.status

  if (typeof status === 'number' && status >= 500 && status < 600) {
    return true
  }

  const { errorMessage, errorDataMessage } = getErrorMessage(error)

  return /\b5\d{2}\b/.test(errorMessage) || /\b5\d{2}\b/.test(errorDataMessage)
}

/**
 * Checks if error is an instant network-layer failure with no HTTP response —
 * e.g. a mobile radio blip surfaced by the browser as "Failed to fetch" / "Load failed".
 * These are safe to retry on read-only requests.
 *
 * Deliberately excludes aborts and timeouts: both surface as AbortError (caught by
 * isAbortError), and we must NOT retry them (IT-2112: "instant network errors only,
 * not on timeouts"). Fail-closed — when unsure, returns false.
 */
const isTransientNetworkError = (error: any): boolean => {
  if (getErrorStatus(error)) {
    return false
  }

  if (isAbortError(error)) {
    return false
  }

  const { errorMessage, errorDataMessage } = getErrorMessage(error)
  return errorMessage.includes(ErrorTypes.NO_RESPONSE) || errorDataMessage.includes(ErrorTypes.NO_RESPONSE)
}

/**
 * Checks if error is a gateway failure (502/503/504) — the request got a response, but from a
 * hop in front of the API rather than from the API itself. The sites reach the API through
 * Vercel: the routing middleware signs the visitor IP and the Nitro route
 * `server/routes/rolfsapi/[...].ts` proxies `/rolfsapi/*` to api.rolfsbuss.se (a vercel.json
 * rewrite did this until #429); when that hop fails Vercel answers 502 "An error occurred with
 * this application." while the API and its load balancer log nothing (IT-3432:
 * roomtypes-for-hotel, 2026-09-03 20:39, 1-4 such reports/day). Read-only requests can be
 * retried once, exactly like a no-response network blip.
 */
const isGatewayError = (error: any): boolean => {
  const status = getErrorStatus(error)
  return status === 502 || status === 503 || status === 504
}

/**
 * Checks if error is a 4xx client error (bad request, validation errors)
 */
const isClientError = (error: any): boolean => {
  if (isCalendarTimeoutError(error)) {
    return false
  }

  const status = error?.status || error?.statusCode || error?.response?.status
  return typeof status === 'number' && status >= 400 && status < 500
}

const isWaitinglistError = (error: any): boolean => {
  return error?.name === ErrorTypes.WAITINGLIST ||
    error?.waitinglist === true ||
    error?.data?.waitinglist === true
}

const isInvalidTripError = (error: any): boolean => {
  return error?.name === ErrorTypes.INVALID_TRIP ||
    getErrorStatus(error) === 404
}

const isStateExpiredError = (error: any): boolean => {
  return error?.name === ErrorTypes.STATE_EXPIRED ||
    error?.data?.message === ErrorTypes.STATE_EXPIRED_MESSAGE ||
    error?.message === ErrorTypes.STATE_EXPIRED_MESSAGE
}

/**
 * The backend refused the caller's credentials or code (401/403): an expected answer, not a failure of ours.
 */
const isUnauthorizedError = (error: any): boolean => {
  const status = getErrorStatus(error)

  return status === 401 || status === 403
}

const isInvalidBookingPhoneError = (error: any): boolean => {
  return getErrorStatus(error) === 422 && Array.isArray(error?.data?.invalidPhoneFields)
}

const isNoRoomsFoundError = (error: any): boolean => {
  const { errorMessage, errorDataMessage } = getErrorMessage(error)

  return [errorDataMessage, errorMessage].includes('No rooms found') ||
    [errorDataMessage, errorMessage].includes('Det finns tyvärr inga bokningsbara rum.') ||
    error?.localeKey === ErrorLocaleKey.NO_ROOMS_FOUND ||
    error?.data?.localeKey === ErrorLocaleKey.NO_ROOMS_FOUND
}

/**
 * The flight offer endpoint answers 404 tagged 'No result' when the package has no recommended
 * flight, and the flight-expired localeKey once that flight stopped being bookable. Both are
 * expected no-result outcomes the UI handles, not failures to report.
 */
const isNoFlightAvailableError = (error: any): boolean => {
  if (getErrorStatus(error) === 404 && error?.data?.error === true) {
    return true
  }

  const tags = error?.data?.tags
  if (Array.isArray(tags) && tags.includes(ErrorTag.NO_RESULT)) {
    return true
  }

  const localeKey = error?.data?.localeKey || error?.localeKey
  return localeKey === ErrorLocaleKey.FLIGHT_EXPIRED
}

/**
 * Classifies a fetch error into a typed service error.
 * Call this after handling abort errors and Sentry capture, then throw the result.
 * Handles: CalendarTimeout, Waitinglist, ServiceDown, and returns unknown errors as-is.
 */
const identifyFetchError = (error: any): Error => {
  if (isCalendarTimeoutError(error)) {
    return new CalendarTimeoutError(error)
  }

  if (isWaitinglistError(error)) {
    return new WaitinglistError()
  }

  if (isServiceDownError(error)) {
    return new ServiceDownError(error)
  }

  return error
}

/**
 * Decides whether an error must be kept out of the error-report endpoint, and returns the Sentry
 * message to log instead of filing a department warning. These are all non-events for the
 * department: nothing broke on the backend and nothing needs a human, so reporting them only
 * buries the real warnings.
 */
const getReportSuppressionMessage = (body: ErrorReportBody, sourceError?: unknown): string | null => {
  /**
   * A no-response client transport error (browser "Failed to fetch" / "Load failed") that
   * survived the read-only retries never reached the server, so it carries no HTTP status or
   * body and is not an actionable backend alert (IT-2113). Same classifier the retry uses, so
   * we suppress exactly what we retried — only report errors that have a status or a body.
   */
  if (isTransientNetworkError(sourceError)) {
    return 'Suppressed client transport error report'
  }

  /**
   * A request we cancelled ourselves — a superseded AbortController, a component tearing down —
   * never failed: no backend problem, no customer impact. Timeout aborts are deliberately NOT
   * suppressed here; they are genuine failures.
   */
  if (isManualAbortError(sourceError)) {
    return 'Suppressed cancelled request error report'
  }

  /**
   * An expired booking state means the customer sat idle past the state's lifetime. The backend
   * answered correctly with state_expired and the pinkscreen tells them to start over, so there
   * is nothing to fix. Checked against the response body too, because the pinkscreen reports
   * that directly and does not always carry the originating error.
   */
  if (isStateExpiredError(sourceError) || isStateExpiredError(body.response)) {
    return 'Suppressed expired booking state error report'
  }

  return null
}

// ======== ERROR MESSAGE HELPERS ========

const getTranslatedErrorMessage = (data: Record<string, string>, t: PluginsInjections['$t']): string => {
  if (!data) {
    return t('serverError') as string
  }

  return String(data.localeKey
    ? t(data.localeKey)
    : (data.message ?? t('serverError')))
}

const buildPinkScreenPayload = (error: any, endpoint?: string): BookErrorScreen => {
  const isTimeout = error?.name === 'AbortError' || error?.type === 'aborted'
  const errorData = error?.data as undefined | { message?: string; technical_error_description?: string }
  const rawMessage = errorData?.message || errorData || error?.message || error
  const errorMessage = typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage)

  return {
    errorMessage,
    technicalErrorDescription: isTimeout ? 'Request timed out' : errorData?.technical_error_description,
    status: error?.status,
    endpoint,
    response: getBackendResponse(error),
    error,
    isNetworkError: isTransientNetworkError(error),
  }
}

const getErrorMessage = (error: any): ErrorMessages => {
  return {
    errorMessage: error?.message || '',
    errorDataMessage: error?.data?.message || error?.response?.data?.message || '',
  }
}

/**
 * Extracts the raw backend response body that triggered an error, so it can be
 * forwarded to the error-report endpoint. Handles both raw ofetch errors
 * (error.data) and our wrapped service errors (error.originalError.data).
 */
const getBackendResponse = (error: any): unknown =>
  error?.data ?? error?.originalError?.data ?? null

/**
 * What a service catch records about a failed request. A timeout, a cancelled request and an
 * HTML gateway page carry no JSON body, so recording only error.data left those events without
 * any details; the status and the fetch error's own message say what actually happened.
 */
const getErrorDetails = (error: any): Record<string, unknown> => ({
  status: getErrorStatus(error),
  message: getErrorMessage(error).errorMessage,
  data: getBackendResponse(error),
})

/**
 * The backend refuses to create a second Zaver link while the payer has already committed to
 * one (IT-2208). That is not a failure to report — the payment exists and the payer only needs
 * to be told about it — so the flag is matched instead of the message text.
 */
const isZaverPaymentAlreadyPendingError = (err: any): boolean =>
  err?.data?.zaverPaymentAlreadyPending === true

const getZaverErrorMessage = (err: any): string => {
  const rawMessage = (err as { data?: { message?: string } })?.data?.message

  if (typeof rawMessage !== 'string') {
    return ''
  }

  const match = rawMessage.match(/"errorMessage":"([^"]+)"/)
  return match?.[1] ?? ''
}

const getErrorStatus = (error: any): number | undefined => {
  return error?.status || error?.statusCode || error?.response?.status
}

const isEmptyValue = (value: any): boolean =>
  value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)

const serializeSearchDataEntry = (key: string, value: any): string =>
  typeof value === 'object' ? `${key}: ${JSON.stringify(value)}` : `${key}: ${value}`

const buildErrorDescription = (error: any, searchData?: Record<string, any>, operation?: string): string => {
  const errorParts = getErrorParts(error)
  const searchParts = getSearchDataParts(searchData)
  const operationPart = operation ? [`Operation: ${operation}`] : []

  return [...operationPart, ...errorParts, ...searchParts].join(' | ')
}

const getErrorParts = (error: any): string[] => {
  const { errorDataMessage, errorMessage } = getErrorMessage(error)

  const primaryMessage = errorDataMessage || errorMessage || 'No error message'
  const status = getErrorStatus(error)
  const responseBody = getResponseBody(error, errorDataMessage)
  const cause = getCauseDetails(error?.cause, primaryMessage)

  return [
    primaryMessage,
    status ? `Status: ${status}` : '',
    responseBody ? `Response: ${responseBody}` : '',
    ...cause,
  ].filter(Boolean)
}

const getResponseBody = (error: any, errorDataMessage: string): string => {
  if (errorDataMessage || !error?.data) {
    return ''
  }

  return JSON.stringify(error.data)
}

const getCauseDetails = (cause: any, primaryMessage: string): string[] => {
  if (!cause) {
    return []
  }

  const causeMessage = cause.message || String(cause)
  const isRedundant = primaryMessage.includes(causeMessage)

  return [
    !isRedundant ? `Cause: ${causeMessage}` : '',
    cause.code ? `Code: ${cause.code}` : '',
  ]
}

const getSearchDataParts = (searchData?: Record<string, any>): string[] => {
  if (!searchData) {
    return []
  }

  return Object.entries(searchData)
    .filter(([, value]) => !isEmptyValue(value))
    .map(([key, value]) => serializeSearchDataEntry(key, value))
}

const getConfirmationErrorMessage = (e: ConfirmationFetchError): string => {
  if (e.data?.error) {
    return 'getInfo failed'
  }

  return 'getInfo threw error'
}

const getConfirmationErrorContext = (e: ConfirmationFetchError, ocr: string | string[], k: string | string[]): Record<string, unknown> => {
  if (e.data?.error) {
    return { details: e.data, params: { ocr, k } }
  }
  return { e, params: { ocr, k } }
}

export {
  ErrorLocaleKey,

  isAbortError,
  isTimeoutAbortError,
  isManualAbortError,
  isCalendarTimeoutError,
  isServiceDownError,
  isTransientNetworkError,
  isGatewayError,
  isClientError,
  isWaitinglistError,
  isInvalidTripError,
  isStateExpiredError,
  isUnauthorizedError,
  isInvalidBookingPhoneError,
  isNoRoomsFoundError,
  isNoFlightAvailableError,
  identifyFetchError,
  getReportSuppressionMessage,

  getTranslatedErrorMessage,
  getErrorMessage,
  getBackendResponse,
  getErrorDetails,
  getZaverErrorMessage,
  isZaverPaymentAlreadyPendingError,
  getConfirmationErrorMessage,
  getConfirmationErrorContext,
  getErrorStatus,

  buildPinkScreenPayload,
  buildErrorDescription,
}